import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { FileText, Rocket, Target } from "@phosphor-icons/react";

const PRIORITY_COLOR = {
  high: "text-[#FF3B30] border-[#FF3B30]/30",
  medium: "text-[#FFB000] border-[#FFB000]/30",
  low: "text-slate-400 border-slate-500/30",
};

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [recs, setRecs] = useState([]);
  const [kind, setKind] = useState("operational");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [r, rec] = await Promise.all([api.get("/reports"), api.get("/recommendations")]);
    setReports(r.data); setRecs(rec.data);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/reports/generate?kind=${kind}`);
      toast.success("Report generated");
      load();
    } catch (e) { toast.error("Failed to generate"); }
    finally { setBusy(false); }
  };

  const decide = async (id, status) => {
    await api.post(`/recommendations/${id}/decision`, { status });
    toast.success(`Recommendation ${status}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="data-label">Delivery</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Reports & Recommendations</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Actionable improvement proposals with impact/effort scoring, plus generated reports for stakeholders.</p>
      </div>

      <Tabs defaultValue="recs">
        <TabsList className="bg-[#13151A] border border-[#272A35]" data-testid="reports-tabs">
          <TabsTrigger data-testid="tab-recs" value="recs">Recommendations</TabsTrigger>
          <TabsTrigger data-testid="tab-reports" value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="recs" className="mt-4 space-y-3">
          {recs.map((r) => (
            <div key={r.id} data-testid={`rec-${r.id}`} className="itsme-card p-5 itsme-card-hover">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Target size={16} className="text-[#00E5FF]" />
                    <div className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${PRIORITY_COLOR[r.priority]}`}>
                      {r.priority} priority
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">status: {r.status}</div>
                  </div>
                  <div className="font-heading text-lg font-semibold mt-2">{r.title}</div>
                  <div className="text-sm text-slate-300 mt-1">{r.description}</div>
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <ImpactBar label="Impact" value={r.impact_score} color="#00E5FF" />
                    <ImpactBar label="Effort" value={r.effort_score} color="#FFB000" />
                  </div>
                  <div className="mt-3">
                    <div className="data-label">Workable steps</div>
                    <ol className="mt-2 text-xs text-slate-300 list-decimal list-inside space-y-1">
                      {(r.steps || []).map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </div>
                </div>
                <div className="flex md:flex-col gap-2">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" data-testid={`approve-${r.id}`} onClick={() => decide(r.id, "approved")}
                              className="bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30 hover:bg-[#00E676]/20">
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`reject-${r.id}`}
                              onClick={() => decide(r.id, "rejected")}
                              className="border-[#272A35] text-slate-300">
                        Reject
                      </Button>
                    </>
                  )}
                  {r.status === "approved" && (
                    <Button size="sm" data-testid={`implement-${r.id}`}
                            onClick={() => decide(r.id, "implemented")}
                            className="bg-[#0055FF] hover:bg-[#0044cc]">
                      <Rocket size={14} className="mr-1" /> Mark implemented
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-4 space-y-4">
          <div className="itsme-card p-4 flex items-center gap-3">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="report-kind" className="w-56 bg-[#0F1117] border-[#272A35]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#13151A] border-[#272A35] text-slate-100">
                <SelectItem value="operational">Operational</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="performance">Performance</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="generate-report" onClick={generate} disabled={busy}
                    className="bg-[#0055FF] hover:bg-[#0044cc]">
              <FileText size={14} className="mr-2" /> {busy ? "Generating…" : "Generate report"}
            </Button>
          </div>
          {reports.length === 0 && <div className="text-sm text-slate-500 p-4">No reports yet.</div>}
          {reports.map((r) => (
            <div key={r.id} data-testid={`report-${r.id}`} className="itsme-card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="data-label">{r.kind}</div>
                  <div className="font-heading text-lg font-semibold mt-1">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-1">Generated by {r.created_by} · {new Date(r.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div className="text-sm text-slate-300 mt-3">{r.summary}</div>
              <div className="mt-4 grid md:grid-cols-3 gap-3">
                {r.sections?.map((s, i) => (
                  <div key={i} className="itsme-card p-3">
                    <div className="data-label">{s.heading}</div>
                    <div className="text-xs text-slate-300 mt-2">{s.body}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ImpactBar = ({ label, value, color }) => (
  <div>
    <div className="flex items-center justify-between">
      <span className="data-label">{label}</span>
      <span className="text-xs font-mono" style={{ color }}>{value}</span>
    </div>
    <div className="h-1.5 rounded-full bg-[#272A35] mt-2 overflow-hidden">
      <div className="h-full" style={{ width: `${value}%`, background: color }} />
    </div>
  </div>
);
